import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import { services } from '../../utils/servicesData';

export default function SharedNavbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [svcOpen, setSvcOpen]   = useState(false);
  const [mobSvc, setMobSvc]     = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSvcOpen(false);
    setMobSvc(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/projects', label: 'Portfolio' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header style={{
      position: 'fixed', top: 0, left: 0, right: 0,
      zIndex: 1000, padding: '0.75rem 1.5rem',
      display: 'flex', justifyContent: 'center',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        width: '100%', maxWidth: 1160,
        background: scrolled ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderRadius: 'var(--radius-pill)',
        padding: '0.6rem 0.75rem 0.6rem 1.25rem',
        boxShadow: scrolled ? 'var(--shadow-md)' : 'var(--shadow-sm)',
        border: '1px solid var(--border)',
        transition: 'var(--transition-slow)',
      }}>

        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.png" alt="SQS"
            style={{ height: 36, objectFit: 'contain' }}
            onError={e => e.target.style.display = 'none'} />
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--navy)', lineHeight: 1.1, letterSpacing: '-0.01em' }}>
              Smart Quality Systems
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav id="nav-desk" style={{ display: 'flex', alignItems: 'center', gap: '0.1rem' }}>
          {navLinks.slice(0, 2).map(l => (
            <Link key={l.to} to={l.to}
              style={{
                padding: '0.45rem 0.9rem', borderRadius: 'var(--radius-pill)',
                fontSize: '0.875rem', fontWeight: isActive(l.to) ? 600 : 500,
                color: isActive(l.to) ? 'var(--navy)' : 'var(--txt-muted)',
                background: isActive(l.to) ? 'rgba(11,37,69,0.07)' : 'transparent',
                textDecoration: 'none', transition: 'var(--transition)',
              }}
              onMouseEnter={e => { if (!isActive(l.to)) { e.currentTarget.style.color = 'var(--navy)'; e.currentTarget.style.background = 'var(--ivory-2)'; } }}
              onMouseLeave={e => { if (!isActive(l.to)) { e.currentTarget.style.color = 'var(--txt-muted)'; e.currentTarget.style.background = 'transparent'; } }}>
              {l.label}
            </Link>
          ))}

          {/* Services dropdown */}
          <div style={{ position: 'relative' }}
            onMouseEnter={() => setSvcOpen(true)}
            onMouseLeave={() => setSvcOpen(false)}>
            <Link to="/services"
              style={{
                padding: '0.45rem 0.9rem', borderRadius: 'var(--radius-pill)',
                fontSize: '0.875rem', fontWeight: isActive('/services') ? 600 : 500,
                color: isActive('/services') ? 'var(--navy)' : 'var(--txt-muted)',
                background: isActive('/services') ? 'rgba(11,37,69,0.07)' : 'transparent',
                textDecoration: 'none', transition: 'var(--transition)',
                display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
              }}>
              Services
              <FiChevronDown style={{
                fontSize: '0.8rem', transition: 'transform 0.2s',
                transform: svcOpen ? 'rotate(180deg)' : 'none',
              }} />
            </Link>
            <AnimatePresence>
              {svcOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
                    width: 560, background: 'var(--white)', borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-xl)', padding: '0.75rem',
                    border: '1px solid var(--border)', zIndex: 200,
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem',
                  }}>
                  {services.map(s => (
                    <Link key={s.id} to={`/services/${s.id}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.65rem',
                        padding: '0.65rem 0.85rem', borderRadius: 'var(--radius-md)',
                        color: 'var(--txt-body)', transition: 'var(--transition)',
                        textDecoration: 'none', fontSize: '0.82rem', fontWeight: 500,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--ivory-2)'; e.currentTarget.style.color = 'var(--navy)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--txt-body)'; }}>
                      <span style={{ fontSize: '1rem', color: 'var(--gold)', flexShrink: 0 }}>{s.icon}</span>
                      {s.title}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navLinks.slice(2).map(l => (
            <Link key={l.to} to={l.to}
              style={{
                padding: '0.45rem 0.9rem', borderRadius: 'var(--radius-pill)',
                fontSize: '0.875rem', fontWeight: isActive(l.to) ? 600 : 500,
                color: isActive(l.to) ? 'var(--navy)' : 'var(--txt-muted)',
                background: isActive(l.to) ? 'rgba(11,37,69,0.07)' : 'transparent',
                textDecoration: 'none', transition: 'var(--transition)',
              }}
              onMouseEnter={e => { if (!isActive(l.to)) { e.currentTarget.style.color = 'var(--navy)'; e.currentTarget.style.background = 'var(--ivory-2)'; } }}
              onMouseLeave={e => { if (!isActive(l.to)) { e.currentTarget.style.color = 'var(--txt-muted)'; e.currentTarget.style.background = 'transparent'; } }}>
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0 }}>
          <Link to="/book-service"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              background: 'var(--navy)', color: 'var(--white)',
              padding: '0.55rem 1.25rem', borderRadius: 'var(--radius-pill)',
              fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none',
              border: '2px solid var(--navy)', transition: 'var(--transition)',
              fontFamily: 'var(--font-sans)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--navy-dark)'; e.currentTarget.style.borderColor = 'var(--navy-dark)'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(11,37,69,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--navy)'; e.currentTarget.style.borderColor = 'var(--navy)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
            Book Consultation
          </Link>
          <button id="nav-ham" onClick={() => setMenuOpen(!menuOpen)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--navy)', fontSize: '1.3rem', padding: '0.3rem', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
            {menuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: '1rem', right: '1rem',
              background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '1rem',
              boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border)', zIndex: 999,
            }}>
            {navLinks.slice(0, 2).map(l => (
              <Link key={l.to} to={l.to}
                style={{
                  display: 'block', padding: '0.7rem 1rem', borderRadius: 'var(--radius-md)',
                  color: isActive(l.to) ? 'var(--navy)' : 'var(--txt-body)',
                  fontWeight: isActive(l.to) ? 700 : 400, textDecoration: 'none',
                  background: isActive(l.to) ? 'var(--ivory-2)' : 'transparent', marginBottom: '0.1rem',
                }}>
                {l.label}
              </Link>
            ))}

            {/* Services accordion */}
            <div>
              <button onClick={() => setMobSvc(!mobSvc)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.7rem 1rem', borderRadius: 'var(--radius-md)',
                  color: 'var(--txt-body)', background: 'transparent', border: 'none',
                  cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: '1rem', marginBottom: '0.1rem',
                }}>
                Services
                <FiChevronDown style={{ transition: 'transform 0.2s', transform: mobSvc ? 'rotate(180deg)' : 'none', color: 'var(--gold)' }} />
              </button>
              {mobSvc && (
                <div style={{ paddingLeft: '0.75rem', paddingBottom: '0.5rem' }}>
                  {services.map(s => (
                    <Link key={s.id} to={`/services/${s.id}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.6rem',
                        padding: '0.5rem 0.75rem', color: 'var(--txt-muted)',
                        fontSize: '0.85rem', borderRadius: 'var(--radius-sm)',
                        textDecoration: 'none', marginBottom: '0.05rem',
                      }}>
                      <span style={{ color: 'var(--gold)' }}>{s.icon}</span>{s.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navLinks.slice(2).map(l => (
              <Link key={l.to} to={l.to}
                style={{
                  display: 'block', padding: '0.7rem 1rem', borderRadius: 'var(--radius-md)',
                  color: isActive(l.to) ? 'var(--navy)' : 'var(--txt-body)',
                  fontWeight: isActive(l.to) ? 700 : 400, textDecoration: 'none',
                  background: isActive(l.to) ? 'var(--ivory-2)' : 'transparent', marginBottom: '0.1rem',
                }}>
                {l.label}
              </Link>
            ))}

            <Link to="/book-service"
              style={{
                display: 'flex', justifyContent: 'center', marginTop: '0.75rem',
                background: 'var(--navy)', color: 'var(--white)',
                padding: '0.75rem', borderRadius: 'var(--radius-md)',
                fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem',
              }}>
              Book a Consultation
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media(min-width:901px){
          #nav-desk { display:flex!important; }
          #nav-ham  { display:none!important; }
        }
        @media(max-width:900px){
          #nav-desk { display:none!important; }
          #nav-ham  { display:flex!important; }
        }
      `}</style>
    </header>
  );
}